import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { danger } from 'redux-store/reducers/common-reducer';
import { uploadDeliverableAttachments } from 'modules/deliverables/reducers/deliverableMediaAttachments';

import { API, get } from 'utils/api';
import { downloadIndividualFile, waitUntilZipIsReadyAndGetPresignedURL } from 'utils/file';
import { logError } from 'utils/log';
import { __t } from 'utils/language';
import { getTempNumericalId } from 'utils/id';
import {
  getAttachmentById,
  getReplacedAttachment,
  getUpdatedAttachmentsFromSnippets,
  getUpdatedParentAttachment
} from '../utils/snippets';

import type {
  DeliverableAttachment,
  DeliverableAttachmentType,
  State
} from 'types';
import {
  Attachment,
  StorySnippetsPayload
} from '../types/general';
import {
  StorySnippetValidationError,
  StorySnippetValidationErrorMap
} from '../types/validation';
import {
  StorySnippetsAddSnippetAction,
  StorySnippetsDeleteDeliverableAttachmentAction,
  StorySnippetsDeleteSnippetAction,
  StorySnippetsExternallyDeleteAllSnippetsAction,
  StorySnippetsFlushDeliverableAction,
  StorySnippetsInitForDeliverableAction,
  StorySnippetsReplaceDeliverableAttachmentAction,
  StorySnippetsUpdateAttachmentsForDeliverableAction,
  StorySnippetsUpdateCanEditForDeliverableAction,
  StorySnippetsValidateDeliverableAction
} from '../types/redux';

export const initForDeliverable = (
  payload: StorySnippetsPayload
): StorySnippetsInitForDeliverableAction => ({
  type: 'story-snippets/init-for-deliverable',
  payload
});

const updateAttachmentsForDeliverable = (
  deliverableId: number,
  snippetId: number,
  type: DeliverableAttachmentType,
  attachments: Array<Attachment>
): StorySnippetsUpdateAttachmentsForDeliverableAction => ({
  type: 'story-snippets/update-attachments-for-deliverable',
  payload: {
    deliverableId,
    attachments,
    snippetId,
    type
  }
});

export const updateCanEditForDeliverable = (
  deliverableId: number,
  canEdit: boolean
): StorySnippetsUpdateCanEditForDeliverableAction => ({
  type: 'story-snippets/update-can-edit-for-deliverable',
  payload: {
    deliverableId,
    canEdit
  }
});

export const updateCanEditAndNotify = (
  deliverableId: number,
  canEdit: boolean
) => (dispatch, getState: () => State) => {
  const { deliverables } = getState().storySnippets;
  const deliverableData = deliverables[deliverableId];

  if (isNil(deliverableData)) {
    throw new Error(`StorySnippets / Can't update canEdit of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  dispatch(updateCanEditForDeliverable(deliverableId, canEdit));
  deliverableData.onStorySnippetEdit(canEdit);
};

export const updateAttachments = (
  deliverableId: number,
  snippetId: number,
  attachments: Array<Attachment>,
  type: DeliverableAttachmentType
) => (dispatch, getState: () => State) => {
  if (attachments.length === 0) {
    throw new Error('StorySnippets / No attachments provided!');
  }

  const { deliverables } = getState().storySnippets;
  const deliverableData = deliverables[deliverableId];

  if (isNil(deliverableData)) {
    throw new Error(`StorySnippets / Can't update attachments of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  // By some reason temp ids are of a different type (string)
  // and it makes things unnecessary complicated.
  const attachmentsWithNumberIds = attachments.map(att => ({
    ...att,
    id: getTempNumericalId()
  }));

  dispatch(updateAttachmentsForDeliverable(
    deliverableId, snippetId, type, attachmentsWithNumberIds
  ));

  const updatedDeliverableData = getState().storySnippets.deliverables[deliverableId];

  if (isNil(updatedDeliverableData)) {
    throw new Error(`StorySnippets / Can't notify about an update of attachments of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  const {
    attachments: attachmentsFromStore,
    parent: parentAttachment
  } = getUpdatedAttachmentsFromSnippets(
    updatedDeliverableData.snippets,
    attachmentsWithNumberIds.map(att => att.id)
  );

  deliverableData.onAttachmentsUpdate(attachmentsFromStore, type);

  if (parentAttachment) {
    deliverableData.onAttachmentsUpdate([parentAttachment], parentAttachment.meta.type);
  }
};

const deleteSnippet = (
  deliverableId: number,
  snippetId: number
): StorySnippetsDeleteSnippetAction => ({
  type: 'story-snippets/delete-snippet',
  payload: {
    deliverableId,
    snippetId
  }
});


export const deleteEntireSnippet = (
  deliverableId: number,
  snippetId: number
) => (dispatch, getState: () => State) => {
  const { deliverables } = getState().storySnippets;
  const deliverableData = deliverables[deliverableId];

  if (isNil(deliverableData)) {
    throw new Error(`StorySnippets / Can't delete snippet of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  dispatch(deleteSnippet(
    deliverableId, snippetId
  ));
  deliverableData.onAttachmentsDelete(
    deliverableData.snippets
      .filter(snip => snip.id === snippetId)
      .reduce((acc, val) => ([
        ...acc,
        ...(val.story ? [val.story] : []),
        ...val.insights
      ]), [] as Array<DeliverableAttachment>)
  );
};

const deleteDeliverableAttachment = (
  attachmentId: number,
  deliverableId: number,
  snippetId: number
): StorySnippetsDeleteDeliverableAttachmentAction => ({
  type: 'story-snippets/delete-deliverable-attachment',
  payload: {
    attachmentId,
    deliverableId,
    snippetId
  }
});

export const deleteAttachment = (
  attachmentId: number,
  deliverableId: number,
  snippetId: number
) => (dispatch, getState: () => State) => {
  const { deliverables } = getState().storySnippets;
  const deliverableData = deliverables[deliverableId];

  if (isNil(deliverableData)) {
    throw new Error(`StorySnippets / Can't delete attachment of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  const allAttachments = deliverableData.snippets
    .reduce((acc, att) => ([
      ...acc,
      ...(att.story ? [att.story] : []),
      ...att.insights
    ]), [] as Array<Attachment>);
  const attachment = allAttachments.find(att => att.id === attachmentId) ?? null;

  if (isNil(attachment)) {
    throw new Error(`StorySnippets / Can't delete an attachment with id: ${attachmentId}: the attachment not found`);
  }

  const parentAttachment = allAttachments.find(
    att => (att.relatedAssets ?? []).find(id => id === attachmentId)
  ) ?? null;

  dispatch(deleteDeliverableAttachment(
    attachmentId, deliverableId, snippetId
  ));
  deliverableData.onAttachmentsDelete([attachment]);

  if (parentAttachment === null) {
    return;
  }

  const updatedDeliverableData = getState().storySnippets.deliverables[deliverableId];

  if (isNil(updatedDeliverableData)) {
    throw new Error(`StorySnippets / Can't notify about a delition of attachments of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  const updatedParent = getAttachmentById(
    updatedDeliverableData.snippets,
    parentAttachment.id
  );

  if (isNil(updatedParent)) {
    throw new Error('StorySnippets / Can\'t notify about a delition of attachment: updated parent asset not found');
  }

  deliverableData.onAttachmentsUpdate([updatedParent], 'highres');
};

const replaceDeliverableAttachment = (
  deliverableId: number,
  newAttachment: Attachment,
  oldAttachmentId: number,
  snippetId: number
): StorySnippetsReplaceDeliverableAttachmentAction => ({
  type: 'story-snippets/replace-deliverable-attachment',
  payload: {
    deliverableId,
    newAttachment,
    oldAttachmentId,
    snippetId
  }
});

export const replaceAttachment = (
  deliverableId: number,
  newAttachment: Attachment,
  oldAttachmentId: number,
  snippetId: number
) => (dispatch, getState: () => State) => {
  const { deliverables } = getState().storySnippets;
  const deliverableData = deliverables[deliverableId];

  if (isNil(deliverableData)) {
    throw new Error(`StorySnippets / Can't replace attachment of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  const snippet = deliverableData.snippets.find(snip => snip.id === snippetId) ?? null;

  if (isNil(snippet)) {
    throw new Error(`StorySnippets / Can't replace attachment of a snippet with id: ${snippetId}: snippet not found`);
  }

  const attachments = [
    ...(snippet.story ? [snippet.story] : []),
    ...snippet.insights
  ];
  const currentAttachment = attachments.find(att => att.id === oldAttachmentId) ?? null;

  if (isNil(currentAttachment)) {
    throw new Error(`StorySnippets / Can't replace attachment with id: ${oldAttachmentId}: attachment not found`);
  }

  dispatch(replaceDeliverableAttachment(
    deliverableId, newAttachment, oldAttachmentId, snippetId
  ));

  const updatedDeliverableData = getState().storySnippets.deliverables[deliverableId];

  if (isNil(updatedDeliverableData)) {
    throw new Error(`StorySnippets / Can't notify about a replacement of attachments of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  const nextAttachment = getReplacedAttachment(
    updatedDeliverableData.snippets,
    newAttachment,
    oldAttachmentId
  );

  if (isNil(nextAttachment)) {
    throw new Error(`StorySnippets / Can't notify about a replacement of attachment with id: ${newAttachment.id}: attachment not found`);
  }

  const updatedParent = getUpdatedParentAttachment(updatedDeliverableData.snippets, newAttachment);

  deliverableData.onAttachmentReplace(
    nextAttachment,
    currentAttachment,
    nextAttachment.meta.type
  );

  if (updatedParent) {
    deliverableData.onAttachmentsUpdate([updatedParent], 'highres');
  }
};

const addSnippet = (deliverableId: number): StorySnippetsAddSnippetAction => ({
  type: 'story-snippets/add-snippet',
  payload: {
    deliverableId
  }
});

export const addStorySnippet = (
  files: Array<File>,
  deliverableId: number
) => async (dispatch, getState: () => State) => {
  const uploadUrl = Math.random().toString();

  try {
    const { deliverables } = getState().storySnippets;
    const deliverable = deliverables[deliverableId]?.deliverable ?? null;

    if (isNil(deliverable)) {
      throw new Error(`StorySnippets / Can't add story snippet to a deliverable with id: ${deliverableId}: delvierable not found`);
    }

    dispatch(showLoading(uploadUrl));

    const savedAttachments: Array<DeliverableAttachment> = await dispatch(uploadDeliverableAttachments(
      files,
      deliverable,
      'highres',
      uploadUrl
    ));

    savedAttachments.forEach(savedAttachment => {
      if (isNil(savedAttachment)) {
        throw new Error('StorySnippets / Can\'t add story snippet to a deliverable because attachment wasn\'t uploaded');
      }

      const attachment = {
        ...savedAttachment,
        id: savedAttachment.id as number
      };

      dispatch(addSnippet(deliverableId));

      const updatedDeliverables = getState().storySnippets.deliverables;
      const snippet = (updatedDeliverables[deliverableId]?.snippets ?? []).slice(-1)[0] ?? null;

      if (isNil(snippet)) {
        throw new Error('StorySnippets / Can\'t add story snippet to a deliverable because new snippet is not found');
      }

      dispatch(updateAttachments(deliverableId, snippet.id, [attachment], 'highres'));
    });
  } catch (error) {
    logError(error);
    dispatch(danger(__t('collabary_common.errors.unknown_error')));
  } finally {
    dispatch(hideLoading(uploadUrl));
  }
};

export const flushDeliverable = (
  deliverableId: number
): StorySnippetsFlushDeliverableAction => ({
  type: 'story-snippets/flush-deliverable',
  payload: {
    deliverableId
  }
});

export const validateDeliverable = (
  deliverableId: number,
  lastValidateAt: null | number
): StorySnippetsValidateDeliverableAction => ({
  type: 'story-snippets/validate-deliverable',
  payload: {
    deliverableId,
    lastValidateAt
  }
});

export const deleteSnippets = (
  deliverableId: number,
  snippetIds: Array<number>
): StorySnippetsExternallyDeleteAllSnippetsAction => ({
  type: 'story-snippets/externally-delete-snippets',
  payload: {
    deliverableId,
    snippetIds
  }
});

export const downloadSnippetAttachments = (
  deliverableId: number,
  snippetId: number
) => async (dispatch, getState: () => State) => {
  const { deliverables } = getState().storySnippets;
  const deliverableData = deliverables[deliverableId];
  const name = getState().campaignBrief?.brief?.name ?? null;
  const storyId = deliverableData.snippets.find(snip => snip.id === snippetId)?.story?.id;

  if (name === null) {
    throw new Error(`StorySnippets / Can't download attachments of a deliverable with id: ${deliverableId}: campaign name is null`);
  }

  if (isNil(deliverableData)) {
    throw new Error(`StorySnippets / Can't download attachments of a deliverable with id: ${deliverableId}: delvierable not found`);
  }

  if (isNil(storyId)) {
    throw new Error(`StorySnippets / Can't download attachments of a snippet with id: ${snippetId}: snippet not found`);
  }

  const url = API.END_POINTS.CAMPAIGNS.downloadSnippet(
    deliverableData.campaignId,
    deliverableId,
    storyId,
    window.encodeURIComponent(`Collabary - ${name} - story snippet ${snippetId + 1}`),
    window.encodeURIComponent(`Story snippet #${snippetId + 1}`)
  );

  dispatch(showLoading(url));

  try {
    const response = await get(url, {}, { suppressGlobalNotification: true });
    const zipFileS3Url = response?.data.url;
    const jsonFileS3Url = response?.data.statusUrl;

    if (zipFileS3Url && jsonFileS3Url) {
      const presignedURL = await waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url);
      await downloadIndividualFile(presignedURL ?? zipFileS3Url);
    } else {
      throw new Error('Returned an empty response');
    }
  } catch (error) {
    logError(error);
  }

  dispatch(hideLoading(url));
};

export const showSnackBarErrors = (errors: StorySnippetValidationErrorMap) => dispatch => {
  const uniqErrors = uniqBy(Object.keys(errors).reduce((acc, id) => (
    [
      ...acc,
      ...errors[id]
    ]
  ), [] as Array<StorySnippetValidationError>), 'type');

  uniqErrors.forEach(error => {
    dispatch(danger(error.snackError));
  });
};
